import { CountryInfo } from 'pages/insights-pro/country-tab/model';
import { MetricsChart } from 'components/MetricsChart';
import { BarChartRenderer } from 'components/chart/renderer/BarChartRenderer';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { useIntl } from 'react-intl';
import { formatDateRangeLabel } from 'pages/insights-pro/country-tab/util';
import { getArrayWithIsOthers } from 'pages/insights-pro/common/graphs/getArrayWithIsOthers';
import { TileContent } from 'components/Layout/PageContainer';
import { DataResult } from 'models/data';
import { useModelService } from 'services/model/model.service';
import { useAppStore } from 'hooks/state/useAppStore';

interface CountriesReturnRatesProps {
  total: DataResult;
  countries: CountryInfo[];
  isComparisonActive: boolean;
}

export function CountriesReturnRates({ total, countries, isComparisonActive }: CountriesReturnRatesProps) {
  const { getMeasures } = useModelService();

  const intl = useIntl();
  const { dateRange } = useAppStore();

  const [slsQtyOD, retQtyOD, retQtyRateOD] = getMeasures(['slsQtyOD', 'retQtyExpOD', 'retQtyRateExpOD']);

  const groupedCountries = getArrayWithIsOthers(
    countries,
    [retQtyRateOD.code, slsQtyOD.code, retQtyOD.code],
    intl,
    c => ({
      ...c,
      entry: {
        ...c.entry,
        values: {
          [retQtyRateOD.code]:
            slsQtyOD.getValue(c.entry.values) === 0
              ? 0
              : retQtyOD.getValue(c.entry.values) / slsQtyOD.getValue(c.entry.values),
        },
        compareValues: {
          [retQtyRateOD.code]:
            slsQtyOD.getValue(c.entry.compareValues) === 0
              ? 0
              : retQtyOD.getValue(c.entry.compareValues) / slsQtyOD.getValue(c.entry.compareValues),
        },
      },
    })
  ).sort((a, b) => retQtyRateOD.getValue(b.entry.values) - retQtyRateOD.getValue(a.entry.values));

  const createReturnRateChartConfig = () => {
    const returnRateChartConfig = BarChartConfig.forTBMeasure(retQtyRateOD);

    returnRateChartConfig.data = [
      new ChartDataArray(
        ChartDataType.primary,
        formatDateRangeLabel(dateRange.range, intl),
        [...groupedCountries].reverse().map(country => {
          const data = new ChartData(country.name, retQtyRateOD.getValue(country.entry.values));
          data.color = country.color;
          return data;
        })
      ),
    ];
    if (isComparisonActive) {
      returnRateChartConfig.data.push(
        new ChartDataArray(
          ChartDataType.primary,

          formatDateRangeLabel(dateRange.comparison, intl),
          [...groupedCountries]
            .reverse()
            .map(country => new ChartData(country.name, retQtyRateOD.getValue(country.entry.compareValues))),
          ChartDataComparisonType.comparison
        )
      );
    }

    returnRateChartConfig.data.push(
      new ChartDataArray(
        ChartDataType.average,
        intl.formatMessage({ id: 'general.label.total' }),
        [retQtyRateOD.getValue(total.values)].map(avg => new ChartData('AVG', avg))
      )
    );
    return returnRateChartConfig;
  };

  const returnRateChartRenderer = new BarChartRenderer(createReturnRateChartConfig());

  return (
    <TileContent className="return-rates">
      <MetricsChart renderer={returnRateChartRenderer} height="h-[400px]" />
    </TileContent>
  );
}
